'use client';

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  type ReactNode,
} from 'react';
import posthog from 'posthog-js';
import { EVENTS } from '@attik/posthog';
import { posthogEnvironment } from '@/util/posthog/sharedConfig';
import {
  COMPANY_SWITCH_TRIGGER_KEY,
  DEFERRED_LOAD_MS,
  LOAD_SUMMARY_SETTLE_MS,
  LOAD_SUMMARY_TIMEOUT_MS,
  type DashboardLoadTrigger,
} from './constants';
import { shouldSampleDashboardPerf } from './sample';
import { dashboardWidgetFromUrl } from './urls';

type DashboardPerfContextValue = {
  registerRequestStart: () => void;
  registerRequestEnd: (
    url: string,
    opts: {
      durationMs: number;
      cacheHit: string | null;
      failed: boolean;
      requestId?: string;
    }
  ) => void;
};

const DashboardPerfContext = createContext<DashboardPerfContextValue | null>(null);

function createLoadId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `load_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
}

function readLoadTrigger(): DashboardLoadTrigger {
  if (typeof window === 'undefined') return 'initial';
  const switchTs = localStorage.getItem(COMPANY_SWITCH_TRIGGER_KEY);
  if (switchTs) {
    localStorage.removeItem(COMPANY_SWITCH_TRIGGER_KEY);
    const age = Date.now() - Number(switchTs);
    if (Number.isFinite(age) && age < 60_000) return 'company_switch';
  }
  const nav = performance.getEntriesByType('navigation')[0] as
    | PerformanceNavigationTiming
    | undefined;
  if (nav?.type === 'reload') return 'reload';
  return 'initial';
}

export function DashboardPerfProvider({ children }: { children: ReactNode }) {
  const loadIdRef = useRef(createLoadId());
  const loadStartedAtRef = useRef(Date.now());
  const triggerRef = useRef<DashboardLoadTrigger>('initial');
  const sampledRef = useRef(false);
  const pendingRef = useRef(0);
  const summarySentRef = useRef(false);
  const settleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const timeoutTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const slowestRef = useRef({ endpoint: '', durationMs: 0 });
  const failedCountRef = useRef(0);
  const requestCountRef = useRef(0);
  const widgetsRef = useRef<Set<string>>(new Set());

  const emitSummary = useCallback(() => {
    if (summarySentRef.current || !sampledRef.current) return;
    if (posthogEnvironment() !== 'production') return;
    if (requestCountRef.current === 0) return;

    summarySentRef.current = true;
    posthog.capture(EVENTS.dashboardLoadSummary, {
      load_id: loadIdRef.current,
      trigger: triggerRef.current,
      total_duration_ms: Date.now() - loadStartedAtRef.current,
      slowest_endpoint: slowestRef.current.endpoint,
      slowest_duration_ms: slowestRef.current.durationMs,
      failed_count: failedCountRef.current,
      request_count: requestCountRef.current,
      widgets: [...widgetsRef.current],
    });
  }, []);

  const scheduleSummary = useCallback(() => {
    if (settleTimerRef.current) clearTimeout(settleTimerRef.current);
    settleTimerRef.current = setTimeout(() => {
      if (pendingRef.current === 0) emitSummary();
    }, LOAD_SUMMARY_SETTLE_MS);
  }, [emitSummary]);

  useEffect(() => {
    triggerRef.current = readLoadTrigger();
    sampledRef.current = shouldSampleDashboardPerf(loadIdRef.current);
    if (typeof window !== 'undefined') {
      (window as unknown as { __attikDashboardLoadId?: string }).__attikDashboardLoadId =
        loadIdRef.current;
    }

    timeoutTimerRef.current = setTimeout(emitSummary, LOAD_SUMMARY_TIMEOUT_MS);

    return () => {
      if (settleTimerRef.current) clearTimeout(settleTimerRef.current);
      if (timeoutTimerRef.current) clearTimeout(timeoutTimerRef.current);
    };
  }, [emitSummary]);

  const registerRequestStart = useCallback(() => {
    pendingRef.current += 1;
  }, []);

  const registerRequestEnd = useCallback(
    (
      url: string,
      opts: {
        durationMs: number;
        cacheHit: string | null;
        failed: boolean;
        requestId?: string;
      }
    ) => {
      pendingRef.current = Math.max(0, pendingRef.current - 1);
      const endpoint = dashboardWidgetFromUrl(url);
      const durationMs = Math.round(opts.durationMs);
      const deferred = Date.now() - loadStartedAtRef.current >= DEFERRED_LOAD_MS;

      requestCountRef.current += 1;
      widgetsRef.current.add(endpoint);
      if (opts.failed) failedCountRef.current += 1;
      if (durationMs > slowestRef.current.durationMs) {
        slowestRef.current = { endpoint, durationMs };
      }

      if (sampledRef.current && posthogEnvironment() === 'production') {
        posthog.capture(EVENTS.dashboardApiRequest, {
          load_id: loadIdRef.current,
          endpoint,
          duration_ms: durationMs,
          cache_hit: opts.cacheHit,
          deferred,
          failed: opts.failed,
          request_id: opts.requestId,
        });
      }

      scheduleSummary();
    },
    [scheduleSummary]
  );

  const value = useMemo(
    () => ({ registerRequestStart, registerRequestEnd }),
    [registerRequestStart, registerRequestEnd]
  );

  return (
    <DashboardPerfContext.Provider value={value}>{children}</DashboardPerfContext.Provider>
  );
}

export function useDashboardPerf(): DashboardPerfContextValue | null {
  return useContext(DashboardPerfContext);
}
